import React from "react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Practice from './Practice';
import SlateTourCard from "../components/SlateTourCard";

const TOUR_STEPS = [
    {
        id: "pen",
        targetId: "slate-btn-pen",
        icon: "",
        title: "Pen Tool",
        desc: "Tap to write on the slate. Tap the pen icon again to pick a pen colour, blue or black.",
        position: "below",
    },
    {
        id: "scroll",
        targetId: "slate-btn-scroll",
        icon: "",
        title: "Scroll Mode",
        desc: "Switch to scroll mode to pan around the slate without drawing. Tap the pen again when you are ready to write.",
        position: "below",
    },
    {
        id: "eraser",
        targetId: "slate-btn-eraser",
        icon: "",
        title: "Eraser",
        desc: "Made a mistake? Drag the eraser over ink to remove it.",
        position: "below",
    },
    {
        id: "undo",
        targetId: "slate-btn-undo",
        icon: "",
        title: "Undo",
        desc: "Tap Undo to remove the last stroke, one stroke at a time.",
        position: "below",
    },
    {
        id: "redo",
        targetId: "slate-btn-redo",
        icon: "",
        title: "Redo",
        desc: "Changed your mind? Tap Redo to bring back the stroke you just undid.",
        position: "below",
    },
    {
        id: "clear",
        targetId: "slate-btn-clear",
        icon: "",
        title: "Clear Slate",
        desc: "Clear removes all writing from the slate after confirmation, so you can restart safely.",
        position: "above",
    },
    {
        id: "fullscreen",
        targetId: "slate-btn-fullscreen",
        icon: "",
        title: "Fullscreen",
        desc: "Use fullscreen when you want maximum notebook space on tablet.",
        position: "below",
    },
    {
        id: "tab-slate",
        targetId: "tab-slate",
        icon: "",
        title: "Slate",
        desc: "Write naturally with your stylus, just like a notebook.",
        position: "above",
    },
    {
        id: "tab-matheditor",
        targetId: "tab-matheditor",
        icon: "",
        title: "Math Editor",
        desc: "Prefer typing or checking OCR? Use Math Editor to view and adjust the converted equations.",
        position: "above",
    },
    {
        id: "step-spacing",
        targetId: "slate-handwritten-guide",
        icon: "",
        title: "Writing Steps",
        desc: "Write each math step freely. Leave a small gap between lines so InkSolver can group and recognize each step automatically.",
        position: "below",
    },
    {
        id: "hint",
        targetId: "slate-btn-hint",
        icon: "",
        title: "Ask For Guidance",
        desc: "Tap the glowing bulb when you want a nudge. It follows along where your next step should go.",
        position: "above",
    },
    {
        id: "tutor-chat",
        targetId: null,
        icon: "",
        title: "Tutor Guide",
        desc: "Guidance opens on top of the slate only when you need it. You can collapse it and reopen it from the tutor icon.",
        position: "center",
    },
    {
        id: "analyze",
        targetId: "slate-btn-analyze",
        icon: "",
        title: "Analyze",
        desc: "When your solution is ready, tap Analyze to check the full work and get feedback.",
        position: "above",
    },
    {
        id: "done",
        targetId: null,
        icon: "",
        title: "You're All Set!",
        desc: "Use the slate, ask for guidance when needed, and analyze when you are ready.",
        position: "center",
        isFinal: true,
    },
];

export default function SolveFirstProblem({ apiBase, token, onPageBusyChange }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [tourActive, setTourActive] = useState(false);
    const [tourStep, setTourStep] = useState(0);
    const [spotlightRect, setSpotlightRect] = useState(null);

    useEffect(() => {
        if (location.state?.fromTour) return;
        const checkReady = setInterval(() => {
            // Wait until the Practice component's UI (specifically the slate editor controls) is fully rendered
            if (document.getElementById("slate-btn-pen")) {
                clearInterval(checkReady);
                // Give it just a tiny 100ms buffer to let the browser paint the layout
                setTimeout(() => setTourActive(true), 100);
            }
        }, 100);
        return () => clearInterval(checkReady);
    }, [location.state]);

    useEffect(() => {
        if (!tourActive) return;
        const step = TOUR_STEPS[tourStep];
        const chatbox = document.getElementById("slate-tutor-chatbox");
        const trigger = document.getElementById("slate-tutor-trigger");
        if (step?.id === "tutor-chat" && !chatbox) {
            trigger?.click();
        } else if (step?.id !== "tutor-chat" && chatbox) {
            trigger?.click();
        }
        if (step && step.targetId && step.position !== "center") {
            // Small delay to ensure DOM element is rendered
            setTimeout(() => {
                const el = document.getElementById(step.targetId);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 160);
        }
    }, [tourStep, tourActive]);

    useEffect(() => {
        if (!tourActive) {
            document.body.style.overflow = "";
            return;
        }
        document.body.style.overflow = "hidden";

        const updateSpotlight = () => {
            const step = TOUR_STEPS[tourStep];
            if (!step || !step.targetId || step.position === "center") {
                setSpotlightRect(null);
                return;
            }
            if (step.id === "tutor-chat" && !document.getElementById("slate-tutor-chatbox")) {
                document.getElementById("slate-tutor-trigger")?.click();
            }
            const el = document.getElementById(step.targetId);
            if (!el) {
                setSpotlightRect(null);
                return;
            }
            const rect = el.getBoundingClientRect();
            setSpotlightRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
            });
        };

        updateSpotlight();
        const intervalId = setInterval(updateSpotlight, 500);
        window.addEventListener("scroll", updateSpotlight, true);
        window.addEventListener("resize", updateSpotlight);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener("scroll", updateSpotlight, true);
            window.removeEventListener("resize", updateSpotlight);
            document.body.style.overflow = "";
        };
    }, [tourActive, tourStep]);

    useEffect(() => {
        const handleStartTour = () => {
            setTourStep(0);
            setTourActive(true);
        };

        window.addEventListener("start-demo-tour", handleStartTour);
        return () => {
            window.removeEventListener("start-demo-tour", handleStartTour);
        };
    }, []);

    const handleTourBack = useCallback(() => {
        setTourStep((prev) => Math.max(0, prev - 1));
    }, []);

    const handleTourNext = useCallback(() => {
        const nextIdx = tourStep + 1;
        if (nextIdx >= TOUR_STEPS.length) {
            if (document.getElementById("slate-tutor-chatbox")) {
                document.getElementById("slate-tutor-trigger")?.click();
            }
            setTourActive(false);
        } else {
            setTourStep(nextIdx);
        }
    }, [tourStep]);

    const handleTourSkip = useCallback(() => {
        if (document.getElementById("slate-tutor-chatbox")) {
            document.getElementById("slate-tutor-trigger")?.click();
        }
        setTourActive(false);
    }, []);

    return (
        <div className="solve-first-problem-page">
            <Practice
                apiBase={apiBase}
                token={token}
                onPageBusyChange={onPageBusyChange}
                isDemo={true}
                demoChapterId="linear-equations"
            />

            <div className="solve-first-problem-footer">
                <div style={{ height: "10px" }}></div>
                <div className="solve-first-problem-footer-text">
                    <p className="page-sub">No pressure. Just <em>try</em> to attempt this problem, <strong>explore</strong> and <em style={{ fontFamily: "'Caveat', cursive", fontSize: "18px", color: "var(--primary)", fontStyle: "normal" }}>have fun learning!</em></p>
                </div>
                <button
                    className="primary-button solve-first-problem-cta-btn"
                    onClick={() => navigate("/start-your-journey", { replace: true })}
                >
                    Go to Start Your Journey →
                </button>

                <div className="solve-first-problem-bottom-features">
                    <div className="solve-first-problem-bottom-feature">
                        <div className="solve-first-problem-bottom-icon">🎯</div>
                        <div>
                            <strong>Better Understanding</strong>
                            <p>Learn the why, not just the answer.</p>
                        </div>
                    </div>
                    <div className="solve-first-problem-bottom-feature">
                        <div className="solve-first-problem-bottom-icon">🤖</div>
                        <div>
                            <strong>Instant AI Feedback</strong>
                            <p>Get line-by-line analysis of your work.</p>
                        </div>
                    </div>
                    <div className="solve-first-problem-bottom-feature">
                        <div className="solve-first-problem-bottom-icon">📈</div>
                        <div>
                            <strong>Track Your Growth</strong>
                            <p>See your progress in every concept.</p>
                        </div>
                    </div>
                </div>
            </div>

            {tourActive && (
                <SlateTourCard
                    step={TOUR_STEPS[tourStep]}
                    stepIndex={tourStep}
                    totalSteps={TOUR_STEPS.length}
                    spotlightRect={spotlightRect}
                    onNext={handleTourNext}
                    onBack={handleTourBack}
                    onSkip={handleTourSkip}
                    canGoBack={tourStep > 0}
                />
            )}
        </div>
    );
}